import React, { useState, useEffect } from 'react';
import { Search as SearchIcon, User, Mail, Phone, Building, MapPin, X, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ventro.energy';

const Search = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of selected user IDs

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setIsAuthenticated(!!token);
  }, []);

  const handleSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setShowLoginModal(true);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URL}/api/users/search/?q=${encodeURIComponent(query)}`, { headers });

      const users = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setSearchResults(users);
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 401) {
        setShowLoginModal(true);
      } else {
        setError('Failed to search users');
      }
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    handleSearch(value);
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    const allUserIds = searchResults.map(user => user.id);
    setSelectedUsers(allUserIds);
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="https://ventro.energy/ventro.png" alt="Ventro Logo" className="h-10" />
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-[#002D43]">Platform Search</h1>
                <p className="text-xs text-gray-600 mt-0.5">Search for users across the platform</p>
              </div>
            </div>
            {!isAuthenticated && (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center space-x-2 px-4 py-2 bg-[#002D43] text-white rounded-lg hover:bg-opacity-90 transition-all"
              >
                <LogIn className="h-4 w-4" />
                <span>Login</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-[#002D43]" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              placeholder="Search by name, email, phone, or company..."
              className="w-full pl-14 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#002D43] focus:border-[#002D43] text-lg transition-all"
            />
          </div>
          {searchQuery && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-gray-600">
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2 text-[#002D43]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </span>
                ) : (
                  <>
                    {`Found ${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`}
                    {selectedUsers.length > 0 && (
                      <span className="ml-2 text-[#002D43] font-semibold">
                        ({selectedUsers.length} selected)
                      </span>
                    )}
                  </>
                )}
              </p>
              {searchResults.length > 0 && !loading && (
                <div className="flex items-center space-x-2">
                  {selectedUsers.length === searchResults.length ? (
                    <button
                      onClick={deselectAllUsers}
                      className="text-sm text-gray-600 hover:text-[#002D43] font-medium"
                    >
                      Deselect All
                    </button>
                  ) : (
                    <button
                      onClick={selectAllUsers}
                      className="text-sm text-[#002D43] hover:text-[#004d6b] font-medium"
                    >
                      Select All
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-6 space-y-4">
            {searchResults.map((user) => {
              const isSelected = selectedUsers.includes(user.id);
              return (
                <div
                  key={user.id}
                  className={`bg-white rounded-xl shadow-md border-2 p-6 hover:shadow-xl transition-all duration-200 ${
                    isSelected
                      ? 'border-[#002D43] bg-blue-50'
                      : 'border-gray-200 hover:border-[#002D43]'
                  }`}
                >
                  <div className="flex items-start space-x-5">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUserSelection(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-5 w-5 text-[#002D43] border-gray-300 rounded focus:ring-[#002D43] cursor-pointer"
                      />
                    </div>

                    {/* Avatar */}
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      onClick={() => openUserDetails(user)}
                    >
                      <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#002D43] to-[#004d6b] flex items-center justify-center shadow-md">
                        <User className="h-8 w-8 text-white" />
                      </div>
                    </div>

                    {/* User Info */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => openUserDetails(user)}
                    >
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-xl font-bold text-[#002D43]">
                        {user.first_name} {user.last_name}
                      </h3>
                      {user.role && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#002D43] text-white">
                          {user.role}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{user.username || user.email}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {user.email && (
                        <div className="flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                          <Mail className="h-4 w-4 mr-2 flex-shrink-0 text-[#002D43]" />
                          <span className="truncate font-medium">{user.email}</span>
                        </div>
                      )}
                      {user.phone && (
                        <div className="flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                          <Phone className="h-4 w-4 mr-2 flex-shrink-0 text-[#002D43]" />
                          <span className="font-medium">{user.phone}</span>
                        </div>
                      )}
                      {user.company_name && (
                        <div className="flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                          <Building className="h-4 w-4 mr-2 flex-shrink-0 text-[#002D43]" />
                          <span className="font-medium">{user.company_name}</span>
                        </div>
                      )}
                      {user.address && (
                        <div className="flex items-center text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                          <MapPin className="h-4 w-4 mr-2 flex-shrink-0 text-[#002D43]" />
                          <span className="truncate font-medium">{user.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* No Results */}
        {searchQuery && !loading && searchResults.length === 0 && !error && (
          <div className="mt-6 bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
            <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <SearchIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No results found</h3>
            <p className="text-gray-600">No users found matching "<span className="font-semibold text-[#002D43]">{searchQuery}</span>"</p>
            <p className="text-sm text-gray-500 mt-2">Try searching with a different keyword</p>
          </div>
        )}

        {/* Initial State */}
        {!searchQuery && (
          <div className="mt-6 bg-white rounded-xl shadow-md border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-[#002D43] to-[#004d6b] flex items-center justify-center mx-auto mb-6">
              <SearchIcon className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-[#002D43] mb-2">Welcome to Platform Search</h3>
            <p className="text-gray-600 mb-4">Start typing in the search box above to find users by name, email, phone, or company</p>
            {!isAuthenticated && (
              <button
                onClick={() => navigate('/login')}
                className="mt-4 px-6 py-3 bg-[#002D43] text-white rounded-lg hover:bg-opacity-90 transition-all font-medium"
              >
                Login to Search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#002D43]">Login Required</h2>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="text-gray-400 hover:text-[#002D43] hover:bg-gray-100 rounded-full p-2 transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="mb-8">
                <div className="h-20 w-20 rounded-full bg-[#002D43] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                  <LogIn className="h-10 w-10 text-[#002D43]" />
                </div>
                <p className="text-center text-gray-700 text-lg mb-2">
                  You need to be logged in to search for users
                </p>
                <p className="text-center text-gray-500 text-sm">
                  Please login or register to access the platform search functionality
                </p>
              </div>

              {/* Modal Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/login')}
                  className="flex-1 px-6 py-3 bg-[#002D43] text-white rounded-xl hover:bg-opacity-90 transition-all font-semibold shadow-md hover:shadow-lg"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-800 transition-all font-semibold shadow-md hover:shadow-lg"
                >
                  Register
                </button>
              </div>
              <button
                onClick={() => setShowLoginModal(false)}
                className="w-full mt-3 px-6 py-2 text-gray-600 hover:text-gray-800 transition-all font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#002D43]">User Details</h2>
                <button
                  onClick={closeUserDetails}
                  className="text-gray-400 hover:text-[#002D43] hover:bg-gray-100 rounded-full p-2 transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* User Avatar */}
              <div className="flex items-center space-x-5 mb-8 bg-gradient-to-r from-gray-50 to-white p-6 rounded-xl">
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-[#002D43] to-[#004d6b] flex items-center justify-center shadow-lg">
                  <User className="h-12 w-12 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#002D43] mb-2">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </h3>
                  {selectedUser.role && (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-[#002D43] text-white">
                      {selectedUser.role}
                    </span>
                  )}
                </div>
              </div>

              {/* User Information */}
              <div className="space-y-3">
                {selectedUser.username && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Username</p>
                    <p className="text-base font-medium text-gray-900">{selectedUser.username}</p>
                  </div>
                )}

                {selectedUser.email && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Email</p>
                    <p className="text-base font-medium text-gray-900">{selectedUser.email}</p>
                  </div>
                )}

                {selectedUser.phone && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Phone</p>
                    <p className="text-base font-medium text-gray-900">{selectedUser.phone}</p>
                  </div>
                )}

                {selectedUser.company_name && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Company</p>
                    <p className="text-base font-medium text-gray-900">{selectedUser.company_name}</p>
                  </div>
                )}

                {selectedUser.address && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Address</p>
                    <p className="text-base font-medium text-gray-900">{selectedUser.address}</p>
                  </div>
                )}

                {selectedUser.date_joined && (
                  <div className="bg-gray-50 border-l-4 border-[#002D43] rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Member Since</p>
                    <p className="text-base font-medium text-gray-900">
                      {new Date(selectedUser.date_joined).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end mt-8 pt-6 border-t-2 border-gray-100">
                <button
                  onClick={closeUserDetails}
                  className="px-6 py-3 bg-[#002D43] text-white rounded-xl hover:bg-opacity-90 transition-all font-semibold shadow-md hover:shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
